import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, money } from "../../lib/api";
import { canTransition } from "../../lib/orderStatus";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import AssignDriverDialog from "../../components/AssignDriverDialog";
import { toast } from "sonner";
import {
  UserCog, MessageCircle, Bell, Send, Search, X, ChevronRight,
  Truck, Fuel, Package, MapPin, Calendar, Clock, Download, CalendarClock,
  ChevronLeft, ChevronRight as ChevronRightIcon,
} from "lucide-react";
import { whatsappUrl } from "../../lib/whatsapp";
import { formatWeekdayLong, formatDateLong, isoDateInTz, scheduleUrgency, shortOrderId } from "../../lib/format";
import { computeStageEvents, totalElapsedMs, fmtElapsedClock, elapsedTone } from "../../lib/stageTiming";
import { useCompany } from "../../contexts/CompanyContext";

const STATUSES = [
  { v: "pending", t: "Recibido" },
  { v: "confirmed", t: "Confirmado" },
  { v: "on_the_way", t: "En camino" },
  { v: "delivered", t: "Entregado" },
  { v: "cancelled", t: "Cancelado" },
];
const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  on_the_way: "bg-violet-100 text-violet-800",
  delivered: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-800",
};
const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.v, s.t]));

/** Tono visual de la etiqueta "Fecha programada" según urgencia. */
const SCHEDULE_TONE = {
  immediate: { wrap: "text-neutral-500",       icon: "text-neutral-400", value: "text-neutral-700" },
  upcoming:  { wrap: "text-blue-700",          icon: "text-blue-500",    value: "text-blue-900 font-semibold" },
  today:     { wrap: "text-amber-800",         icon: "text-amber-600",   value: "text-amber-900 font-semibold" },
  overdue:   { wrap: "text-red-700",           icon: "text-red-600",     value: "text-red-800 font-bold" },
};

/** Traducciones para export CSV. */
const PAYMENT_STATUS_LABEL = {
  pending: "Pendiente",
  cash_on_delivery: "Pago contra entrega",
  paid: "Pagado",
  failed: "Fallido",
  cancelled: "Cancelado",
  refunded: "Reembolsado",
};
const CHANNEL_LABEL = {
  autotanque: "Autotanque",
  cilindrera: "Cilindrera",
  estacion: "Estación",
};

const CHANNELS = [
  { v: "all", t: "Todos los canales" },
  { v: "autotanque", t: "Autotanque" },
  { v: "cilindrera", t: "Cilindrera" },
  { v: "estacion", t: "Estación" },
];
const TIME_RANGES = [
  { v: "all", t: "Todo el tiempo" },
  { v: "today", t: "Hoy" },
  { v: "7d", t: "Últimos 7 días" },
  { v: "30d", t: "Últimos 30 días" },
];
const SORTS = [
  { v: "newest", t: "Más recientes" },
  { v: "oldest", t: "Más antiguos" },
  { v: "elapsed", t: "Tiempo desde recepción" },
  { v: "total_desc", t: "Mayor monto" },
  { v: "total_asc", t: "Menor monto" },
];
const PAGE_SIZE = 25;

/** Freezes at final elapsed when delivered/cancelled. */
function Elapsed({ order }) {
  const [now, setNow] = useState(() => Date.now());
  const closed = order?.status === "delivered" || order?.status === "cancelled";
  useEffect(() => {
    if (closed) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [closed]);
  const ms = totalElapsedMs(order, now);
  if (ms == null) return <span className="text-[11px] text-neutral-400">—</span>;
  return <span className={`font-mono text-[11px] tabular-nums ${elapsedTone(ms, { closed })}`}>{fmtElapsedClock(ms)}</span>;
}

/** Lista compacta de tiempos por status (ver stageTiming.js). Usa
 *  chips redondeados con fondo gris para ganar prominencia en la lista. */
function StageAges({ order, className = "" }) {
  const [now, setNow] = useState(() => Date.now());
  const closed = order?.status === "delivered" || order?.status === "cancelled";
  useEffect(() => {
    if (closed) return;
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, [closed]);
  const events = computeStageEvents(order, now);
  if (events.length === 0) return null;
  return (
    <div className={`flex flex-wrap items-center gap-1.5 text-[11px] ${className}`} data-testid={`stage-ages-${order?.order_id || ""}`}>
      {events.map((ev) => (
        <span
          key={ev.status + ev.at}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-neutral-100 border border-neutral-200"
        >
          <span className="font-semibold text-neutral-800">{ev.label}</span>
          <span className="text-neutral-500">hace {ev.ageLabel}</span>
        </span>
      ))}
    </div>
  );
}

const fmtDay = (iso, tz) => formatWeekdayLong(iso, tz);

export default function AdminOrders() {
  const { currentCompany } = useCompany();
  const tz = currentCompany?.timezone;
  const [searchParams, setSearchParams] = useSearchParams();
  const dayFilter = searchParams.get("day") || ""; // "today" → mismo criterio que el embudo
  const [data, setData] = useState({ items: [], total: 0, status_counts: {} });
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignOrderId, setAssignOrderId] = useState(null);
  // Pending WA notifications grouped by order_id (so each row can render
  // its own action chip — never aggregated globally).
  const [notifsByOrder, setNotifsByOrder] = useState({});

  const [statusFilter, setStatusFilter] = useState(() => searchParams.get("status") || "active");
  const [channelFilter, setChannelFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState(() => dayFilter === "today" ? "today" : "all");
  const [sortBy, setSortBy] = useState("newest");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Reset page whenever any filter actually changes — keep the operator
  // anchored to the first results that match their new query.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [statusFilter, channelFilter, timeFilter, search]);

  const sinceIso = useMemo(() => {
    if (timeFilter === "all") return undefined;
    // Calcular "hoy" en la zona horaria de la empresa → ISO UTC para Mongo.
    if (timeFilter === "today") {
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: tz || undefined,
        year: "numeric", month: "2-digit", day: "2-digit",
      }).format(new Date());
      // Midnight local → construir un ISO UTC ajustado. Truco: crear un
      // Date con la fecha local y obtener el offset en ese día.
      // Si no tenemos tz, fallback a comportamiento previo.
      if (tz) {
        try {
          // Usamos toLocaleString como "parser" en reverso.
          const now = new Date();
          const localIsoStr = `${parts}T00:00:00`;
          // Averiguamos el offset del TZ en ese instante.
          const sample = new Date(localIsoStr + "Z");
          const offsetMinutes = (sample - new Date(sample.toLocaleString("en-US", { timeZone: tz }))) / 60000;
          return new Date(sample.getTime() + offsetMinutes * 60000).toISOString();
        } catch { /* fall-through */ }
      }
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d.toISOString();
    }
    const d = new Date();
    if (timeFilter === "7d") d.setDate(d.getDate() - 7);
    else if (timeFilter === "30d") d.setDate(d.getDate() - 30);
    return d.toISOString();
  }, [timeFilter, tz]);

  const load = async () => {
    try {
      const r = await api.get("/orders/admin", {
        params: {
          page,
          page_size: PAGE_SIZE,
          status: statusFilter,
          channel: channelFilter !== "all" ? channelFilter : undefined,
          q: search || undefined,
          since: dayFilter === "today" ? undefined : sinceIso,
          day_filter: dayFilter === "today" ? "delivery_today" : undefined,
        },
      });
      setData(r.data || { items: [], total: 0, status_counts: {} });
      // Pending WA notifs scoped to the orders currently on screen. Cheap:
      // queues are short and we already paginated the orders themselves.
      try {
        const n = await api.get("/wa/notifications", { params: { status: "queued" } });
        const map = {};
        (n.data || []).forEach((x) => {
          map[x.order_id] = map[x.order_id] || [];
          map[x.order_id].push(x);
        });
        setNotifsByOrder(map);
      } catch (_e) { /* non-fatal */ }
    } catch (_e) {
      toast.error("No se pudieron cargar los pedidos");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, channelFilter, timeFilter, sinceIso, search, dayFilter]);

  // Local sort applied on top of server response (server sorts by created_at
  // desc; here we honour the user's choice of sort key for the visible page).
  const sortedItems = useMemo(() => {
    const list = [...(data.items || [])];
    const receivedAt = (o) =>
      (o.status_history || []).find((h) => h.status === "pending")?.at || o.created_at;
    list.sort((a, b) => {
      switch (sortBy) {
        case "oldest": return new Date(a.created_at) - new Date(b.created_at);
        case "elapsed": {
          const ra = (a.status === "delivered" || a.status === "cancelled") ? 0 : Date.now() - new Date(receivedAt(a)).getTime();
          const rb = (b.status === "delivered" || b.status === "cancelled") ? 0 : Date.now() - new Date(receivedAt(b)).getTime();
          return rb - ra;
        }
        case "total_desc": return (b.total || 0) - (a.total || 0);
        case "total_asc":  return (a.total || 0) - (b.total || 0);
        case "newest":
        default:           return new Date(b.created_at) - new Date(a.created_at);
      }
    });
    return list;
  }, [data.items, sortBy]);

  const grouped = useMemo(() => {
    const out = []; const seen = new Map();
    for (const o of sortedItems) {
      const key = isoDateInTz(o.created_at, tz);
      if (!seen.has(key)) {
        const arr = []; seen.set(key, arr);
        out.push({ key, label: fmtDay(o.created_at, tz), items: arr });
      }
      seen.get(key).push(o);
    }
    return out;
  }, [sortedItems]);

  const counts = data.status_counts || {};
  const total = data.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Quick KPIs derived from the (unfiltered) status_counts when timeFilter
  // is "today"/"all". When the user narrows the date filter, counts already
  // reflect that window so the numbers stay coherent with what they see.
  const kpis = useMemo(() => {
    const inFlight = (counts.pending || 0) + (counts.confirmed || 0) + (counts.on_the_way || 0);
    return {
      total: counts.all || 0,
      inFlight,
      delivered: counts.delivered || 0,
      cancelled: counts.cancelled || 0,
    };
  }, [counts]);

  // Handlers ------------------------------------------------------------
  const updateStatus = async (id, status, currentStatus, driverId) => {
    if (!canTransition(currentStatus, status)) { toast.error("Transición no permitida"); return; }
    if (status === "on_the_way" && !driverId) {
      toast.error("Asigna primero una unidad de reparto antes de marcar el pedido como En camino.");
      return;
    }
    try {
      await api.patch(`/orders/${id}/status`, { status });
      toast.success("Estado actualizado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar");
    }
  };
  const openAssign = (id) => { setAssignOrderId(id); setAssignOpen(true); };
  const sendWaNotif = async (notif) => {
    const url = whatsappUrl(notif.client_phone, notif.body);
    if (!url) { toast.error("El cliente no tiene teléfono válido"); return; }
    window.open(url, "_blank", "noreferrer");
    try {
      await api.post(`/wa/notifications/${notif.wa_notif_id}/sent`);
      toast.success("Marcada como enviada"); load();
    } catch { toast.error("No se pudo marcar como enviada"); }
  };
  const dismissWaNotif = async (notif) => {
    try { await api.post(`/wa/notifications/${notif.wa_notif_id}/dismiss`); load(); }
    catch { toast.error("No se pudo descartar"); }
  };

  const clearFilters = () => {
    setStatusFilter("active"); setChannelFilter("all");
    setTimeFilter("all"); setSortBy("newest"); setSearch("");
    setSearchParams({});
  };
  const filtersActive = statusFilter !== "active" || channelFilter !== "all"
    || timeFilter !== "all" || sortBy !== "newest" || search !== "" || dayFilter !== "";

  const exportCsv = () => {
    if (sortedItems.length === 0) { toast.error("No hay pedidos para exportar"); return; }
    const escape = (val) => {
      if (val === null || val === undefined) return "";
      const s = String(val).replace(/"/g, '""');
      return /[",\n;]/.test(s) ? `"${s}"` : s;
    };
    const fmt = (iso) => iso ? formatDateLong(iso, tz) : "";
    const headers = [
      "ID", "Fecha creación", "Estado", "Cliente", "Teléfono",
      "Items", "Cantidad total", "Subtotal", "Total",
      "Dirección", "Distancia (km)", "Canal",
      "Pago", "Estado pago", "Repartidor", "Programado",
      "Recibido", "Confirmado", "En camino", "Entregado", "Cancelado",
      "Motivo cancelación",
    ];
    const rows = sortedItems.map((o) => {
      const items = (o.items || []).map((i) => `${i.quantity}× ${i.name}`).join(" | ");
      const qty = (o.items || []).reduce((acc, i) => acc + (i.quantity || 0), 0);
      const channels = Array.from(new Set(
        (o.items || []).map((i) => i.channel).filter(Boolean)
      )).map((c) => CHANNEL_LABEL[c] || c).join(" / ");
      const histAt = (s) => fmt((o.status_history || []).find((h) => h.status === s)?.at);
      const cancelEv = (o.status_history || []).find((h) => h.status === "cancelled");
      return [
        o.order_id, fmt(o.created_at), STATUS_LABEL[o.status] || o.status,
        o.client_name || "", o.client_phone || "",
        items, qty, o.subtotal ?? "", o.total ?? "",
        o.address?.full_address || "", o.distance_km ?? "", channels,
        o.payment_method === "cash" ? "Efectivo" : "En línea",
        PAYMENT_STATUS_LABEL[o.payment_status] || o.payment_status || "",
        o.driver_snapshot?.name || "",
        o.when === "scheduled" ? fmt(o.scheduled_at) : "Inmediata",
        histAt("pending") || fmt(o.created_at),
        histAt("confirmed"), histAt("on_the_way"), histAt("delivered"), histAt("cancelled"),
        cancelEv?.reason || "",
      ].map(escape).join(",");
    });
    const csv = "\uFEFF" + [headers.map(escape).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url; a.download = `pedidos_${stamp}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exportados ${sortedItems.length} pedido(s) de la página actual`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Pedidos</h1>
          <p className="text-sm text-neutral-500 mt-1">
            {total} pedido{total === 1 ? "" : "s"} · página {page} de {totalPages}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <KpiPill label="Total" value={kpis.total} />
          <KpiPill label="En curso" value={kpis.inFlight} accent="amber" />
          <KpiPill label="Entregados" value={kpis.delivered} accent="emerald" />
          <KpiPill label="Cancelados" value={kpis.cancelled} accent="red" />
        </div>
      </div>

      {/* Status tabs */}
      <div className="mt-5 flex flex-wrap gap-2" data-testid="status-tabs">
        <Tab active={statusFilter === "active"} onClick={() => setStatusFilter("active")} label="En curso" count={counts.active || 0} />
        <Tab active={statusFilter === "all"} onClick={() => setStatusFilter("all")} label="Todos" count={counts.all || 0} />
        {STATUSES.map((s) => (
          <Tab
            key={s.v}
            active={statusFilter === s.v}
            onClick={() => setStatusFilter(s.v)}
            label={s.t}
            count={counts[s.v] || 0}
            tone={STATUS_COLOR[s.v]}
          />
        ))}
      </div>

      {/* Filters row — every control is the same h-10 so they line up. */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto_auto_auto] gap-2">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por cliente, teléfono, ID o dirección…"
            className="h-10 pl-9 pr-9"
            data-testid="orders-search"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700" aria-label="Limpiar búsqueda">
              <X size={14} />
            </button>
          )}
        </div>
        <Select value={channelFilter} onValueChange={setChannelFilter}>
          <SelectTrigger className="h-10 w-44" data-testid="filter-channel"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CHANNELS.map((c) => <SelectItem key={c.v} value={c.v}>{c.t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={timeFilter} onValueChange={setTimeFilter}>
          <SelectTrigger className="h-10 w-44" data-testid="filter-time"><SelectValue /></SelectTrigger>
          <SelectContent>
            {TIME_RANGES.map((r) => <SelectItem key={r.v} value={r.v}>{r.t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="h-10 w-52" data-testid="filter-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            {SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" className="h-10 px-4" onClick={clearFilters} disabled={!filtersActive} data-testid="clear-filters">
          Limpiar
        </Button>
        <Button
          variant="outline"
          className="h-10 px-4"
          onClick={exportCsv}
          disabled={sortedItems.length === 0}
          data-testid="export-csv"
          title={`Exportar ${sortedItems.length} pedido(s) de la página actual a CSV`}
        >
          <Download size={14} className="mr-1.5" /> CSV
        </Button>
      </div>

      {/* Orders list */}
      <div className="mt-6 space-y-6">
        {loading && (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <div key={i} className="h-24 bg-neutral-100 rounded-xl animate-pulse" />)}
          </div>
        )}
        {!loading && sortedItems.length === 0 && (
          <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-neutral-200">
            <Package size={32} className="mx-auto text-neutral-300" />
            <p className="mt-2 font-display font-bold text-lg">Sin pedidos</p>
            <p className="text-sm text-neutral-500 mt-1">
              {filtersActive ? "Prueba ajustando los filtros." : "Aún no hay pedidos en este buzón."}
            </p>
            {filtersActive && (
              <Button variant="outline" className="mt-4" onClick={clearFilters}>Limpiar filtros</Button>
            )}
          </div>
        )}
        {grouped.map((g) => (
          <div key={g.key}>
            <p className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2 sticky top-16 bg-neutral-50 py-1 z-10">
              {g.label}
            </p>
            <div className="space-y-2">
              {g.items.map((o) => {
                const channel = (o.items?.[0]?.channel) || "cilindrera";
                const ChannelIcon = channel === "autotanque" ? Truck : channel === "estacion" ? Fuel : Package;
                const itemsCount = (o.items || []).reduce((acc, it) => acc + (it.quantity || 1), 0);
                const oneLineItems = (o.items || []).slice(0, 2).map((i) => `${i.quantity}× ${i.name}`).join(", ");
                const orderNotifs = notifsByOrder[o.order_id] || [];
                return (
                  <div
                    key={o.order_id}
                    id={`admin-order-${o.order_id}`}
                    className="bg-white border border-neutral-200 rounded-xl px-4 py-3 transition-shadow hover:shadow-sm"
                    data-testid={`admin-order-${o.order_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-neutral-100 flex items-center justify-center shrink-0 text-neutral-600">
                        <ChannelIcon size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold truncate">{o.client_name || "Cliente"}</p>
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_COLOR[o.status]}`}>
                            {STATUS_LABEL[o.status]}
                          </span>
                          <Elapsed order={o} />
                        </div>
                        <p className="text-xs text-neutral-500 truncate">
                          <span className="font-mono">#{shortOrderId(o.order_id)}</span>
                          {" · "}
                          {itemsCount} {itemsCount === 1 ? "producto" : "productos"}
                          {oneLineItems ? ` · ${oneLineItems}${(o.items || []).length > 2 ? "…" : ""}` : ""}
                        </p>
                        <StageAges order={o} className="mt-1" />
                        {o.address?.full_address && (
                          <p className="text-xs text-neutral-500 truncate flex items-center gap-1 mt-0.5">
                            <MapPin size={10} className="shrink-0" />
                            <span className="truncate">{o.address.full_address}</span>
                          </p>
                        )}
                        <p
                          className={`text-xs truncate flex items-center gap-1 mt-0.5 ${SCHEDULE_TONE[scheduleUrgency(o, tz)].wrap}`}
                          data-testid={`order-schedule-${o.order_id}`}
                        >
                          <CalendarClock size={10} className={`shrink-0 ${SCHEDULE_TONE[scheduleUrgency(o, tz)].icon}`} />
                          <span className="truncate">
                            Fecha programada:{" "}
                            <span className={SCHEDULE_TONE[scheduleUrgency(o, tz)].value}>
                              {o.when === "scheduled" && o.scheduled_at
                                ? formatDateLong(o.scheduled_at, tz)
                                : "Inmediata"}
                            </span>
                          </span>
                        </p>
                      </div>
                      {/* Action group — every control is h-9 so they line
                          up perfectly. Asignar uses a fixed width so the
                          "Asignar/Reasignar" toggle doesn't shift the row. */}
                      <div className="hidden md:flex items-center gap-2">
                        <Select value={o.status} onValueChange={(v) => updateStatus(o.order_id, v, o.status, o.driver_id)}>
                          <SelectTrigger className="h-9 w-36" data-testid={`status-${o.order_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => {
                              const blockedNoDriver = s.v === "on_the_way" && !o.driver_id;
                              return (
                                <SelectItem key={s.v} value={s.v} disabled={!canTransition(o.status, s.v) || blockedNoDriver}>
                                  {s.t}{blockedNoDriver ? " (asigna unidad)" : ""}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          className="h-9 w-28 px-0 justify-center text-xs"
                          onClick={() => openAssign(o.order_id)}
                          data-testid={`assign-${o.order_id}`}
                        >
                          <UserCog size={12} className="mr-1" />
                          {o.driver_id ? "Reasignar" : "Asignar"}
                        </Button>
                        {o.client_phone && (
                          <Button
                            asChild
                            variant="outline"
                            className="h-9 w-9 p-0 text-green-700 border-green-200 hover:bg-green-50"
                          >
                            <a
                              href={whatsappUrl(o.client_phone, `Hola${o.client_name ? ` ${o.client_name}` : ""}, sobre tu pedido #${shortOrderId(o.order_id)}`)}
                              target="_blank" rel="noreferrer"
                              data-testid={`wa-${o.order_id}`}
                              aria-label="WhatsApp al cliente"
                            >
                              <MessageCircle size={14} />
                            </a>
                          </Button>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className="font-display font-extrabold whitespace-nowrap">{money(o.total)}</span>
                        <Button asChild size="sm" variant="ghost" className="h-7 text-xs px-2" data-testid={`open-detail-${o.order_id}`}>
                          <Link to={`/admin/orders/${o.order_id}`}>
                            Detalle <ChevronRight size={12} className="ml-0.5" />
                          </Link>
                        </Button>
                      </div>
                    </div>

                    {/* Per-order pending WhatsApp messages — surfaced
                        directly on the row that owns them so the operator
                        can act in context (instead of an aggregated banner). */}
                    {orderNotifs.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-neutral-100 space-y-1">
                        {orderNotifs.map((n) => (
                          <div
                            key={n.wa_notif_id}
                            className="flex items-center gap-2 text-xs bg-green-50 dark:bg-green-900/30 dark:text-green-200 rounded-lg px-2.5 py-1.5"
                            data-testid={`wa-notif-${n.wa_notif_id}`}
                          >
                            <Bell size={11} className="text-green-700 shrink-0" />
                            <span className="flex-1 truncate text-neutral-700">{n.body}</span>
                            <Button size="sm" variant="outline" className="h-7 text-green-700 border-green-300 hover:bg-green-100" onClick={() => sendWaNotif(n)}>
                              <Send size={11} className="mr-1" /> Enviar
                            </Button>
                            <button onClick={() => dismissWaNotif(n)} className="text-[11px] text-green-700/70 hover:text-green-900">
                              descartar
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between" data-testid="pagination">
          <p className="text-xs text-neutral-500">
            Mostrando {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} de {total}
          </p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-9" disabled={page === 1} onClick={() => setPage(1)}>
              Primera
            </Button>
            <Button variant="outline" size="sm" className="h-9 w-9 p-0" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Anterior">
              <ChevronLeft size={14} />
            </Button>
            <span className="px-3 text-sm font-semibold">{page}</span>
            <Button variant="outline" size="sm" className="h-9 w-9 p-0" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Siguiente">
              <ChevronRightIcon size={14} />
            </Button>
            <Button variant="outline" size="sm" className="h-9" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>
              Última
            </Button>
          </div>
        </div>
      )}

      <AssignDriverDialog
        orderId={assignOpen ? assignOrderId : null}
        orderStatus={assignOpen ? (data.items || []).find((o) => o.order_id === assignOrderId)?.status : null}
        open={assignOpen}
        onOpenChange={(o) => setAssignOpen(o)}
        onAssigned={load}
      />
    </div>
  );
}

function Tab({ active, onClick, label, count, tone }) {
  return (
    <button
      onClick={onClick}
      className={`group inline-flex items-center gap-2 px-3 h-9 rounded-full text-sm font-semibold transition-colors tap-fade ${
        active ? "bg-neutral-900 text-white" : "bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-50"
      }`}
      data-testid={`tab-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      {label}
      <span
        className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
          active ? "bg-white/20 text-white" : tone || "bg-neutral-100 text-neutral-600"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function KpiPill({ label, value, accent = "neutral" }) {
  const colors = {
    neutral: "bg-white border-neutral-200 text-neutral-700",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    red: "bg-red-50 border-red-200 text-red-700",
  };
  return (
    <div className={`flex items-center gap-2 px-3 h-10 rounded-full border ${colors[accent]}`}>
      <Calendar size={14} />
      <div className="leading-none">
        <p className="text-[10px] uppercase tracking-widest opacity-70">{label}</p>
        <p className="text-sm font-bold">{value}</p>
      </div>
    </div>
  );
}
